<template>
  <div class="press-links bg-base-200 py-8 px-4 md:px-8 min-h-screen relative">
    <div class="container mx-auto">
      <div class="card bg-base-100 shadow-xl p-4 md:p-8">
        <!-- Header -->
        <div
          class="flex flex-col md:flex-row justify-between items-center mb-8"
        >
          <h2 class="text-4xl font-semibold text-primary mb-4 md:mb-0">
            Press
          </h2>
          <div v-if="loggedIn" class="flex">
            <button
              class="btn btn-primary mr-2"
              @click="showNewQuoteModal = true"
            >
              Add Quote
            </button>
            <button
              class="btn btn-primary"
              @click="showNewPressLinkModal = true"
            >
              Add Press Link
            </button>
          </div>
        </div>

        <!-- Quotes Section -->
        <div v-if="visibleQuotes.length" class="mb-12">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="quote in visibleQuotes"
              :key="quote.id"
              class="bg-base-100 rounded-lg shadow-md p-3 relative"
            >
              <button
                v-if="loggedIn"
                class="btn btn-warning btn-xs absolute top-2 right-2"
                @click="editQuote(quote.id)"
              >
                <i class="fas fa-pencil-alt text-xs"></i>
              </button>
              <blockquote class="text-base italic text-base-content">
                "{{ quote.quote }}"
              </blockquote>
              <p class="text-right text-primary text-sm mt-2">
                - {{ quote.publisher }}
              </p>
            </div>
          </div>
        </div>

        <!-- Articles Section -->
        <div>
          <h3 class="text-3xl font-bold text-center text-base-content mb-6">
            Articles
          </h3>
          <div class="space-y-6">
            <div
              v-for="(link, index) in visiblePressLinks"
              :key="link.id"
              class="flex flex-col md:flex-row justify-between items-start relative py-4"
              :class="{
                'border-b border-base-content border-opacity-20':
                  index < visiblePressLinks.length - 1,
              }"
            >
              <div class="flex-grow">
                <h4 class="text-2xl font-bold text-base-content mb-2">
                  {{ link.title }}
                </h4>
                <a
                  :href="link.link"
                  target="_blank"
                  class="text-primary hover:text-primary-focus transition duration-300 break-all"
                  >{{ link.link }}</a
                >
                <p class="text-base-content text-sm mt-2">
                  {{ formatDate(link.date) }}
                </p>
              </div>
              <button
                v-if="loggedIn"
                class="btn btn-warning btn-sm mt-4 md:mt-0 md:ml-4"
                @click="editPressLink(link.id)"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <div v-if="showNewPressLinkModal" class="modal modal-open">
      <div class="modal-box">
        <h2 class="text-2xl font-bold">
          {{ isEdit ? 'Update' : 'Create' }} Press Link
        </h2>
        <div class="form-control">
          <label class="label"><span class="label-text">Title</span></label>
          <input
            v-model="newPressLink.title"
            type="text"
            placeholder="Article Title"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label"><span class="label-text">Link</span></label>
          <input
            v-model="newPressLink.link"
            type="text"
            placeholder="https://example.com/article"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label"><span class="label-text">Date</span></label>
          <input
            v-model="newPressLink.date"
            type="date"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label cursor-pointer"
            ><span class="label-text">Visible</span>
            <input
              v-model="newPressLink.visible"
              type="checkbox"
              class="checkbox"
          /></label>
        </div>
        <div class="modal-action">
          <button class="btn btn-ghost" @click="showNewPressLinkModal = false">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            :disabled="!canSavePressLink"
            @click="savePressLink"
          >
            {{ isEdit ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showNewQuoteModal" class="modal modal-open">
      <div class="modal-box">
        <h2 class="text-2xl font-bold">
          {{ isEdit ? 'Update' : 'Create' }} Quote
        </h2>
        <div class="form-control">
          <label class="label"><span class="label-text">Quote</span></label>
          <input
            v-model="newQuote.quote"
            type="text"
            placeholder="This band is amazing!"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label"><span class="label-text">Publisher</span></label>
          <input
            v-model="newQuote.publisher"
            type="text"
            placeholder="A Very Real Magazine"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label cursor-pointer"
            ><span class="label-text">Visible</span>
            <input v-model="newQuote.visible" type="checkbox" class="checkbox"
          /></label>
        </div>
        <div class="modal-action">
          <button class="btn btn-ghost" @click="showNewQuoteModal = false">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            :disabled="!canSaveQuote"
            @click="saveQuote"
          >
            {{ isEdit ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { pressCollection, quotesCollection } from '../firebase'
import { addDoc, doc, updateDoc } from 'firebase/firestore'
import { useToast } from '@/composables/useToast'

const store = useStore()

const isEdit = ref(false)
const newPressLink = ref({ date: new Date(), visible: true })
const newQuote = ref({ visible: true })
const showNewPressLinkModal = ref(false)
const showNewQuoteModal = ref(false)
const { showToast } = useToast()

const pressLinks = computed(() => store.getters['press/pressLinks'])
const quotes = computed(() => store.getters['press/quotes'])
const loggedIn = computed(() => store.getters.loggedIn)

const visiblePressLinks = computed(() =>
  pressLinks.value.filter((link) => link.visible)
)
const visibleQuotes = computed(() =>
  quotes.value.filter((quote) => quote.visible)
)

const canSavePressLink = computed(
  () =>
    newPressLink.value.link &&
    newPressLink.value.title &&
    newPressLink.value.date
)
const canSaveQuote = computed(
  () => newQuote.value.quote && newQuote.value.publisher
)

const formatDate = (date) => {
  if (date && date.toDate) {
    return date.toDate().toLocaleDateString('en-US')
  }
  if (date) {
    return new Date(date).toLocaleDateString('en-US')
  }
  return ''
}

const editPressLink = (pressId) => {
  isEdit.value = true
  showNewPressLinkModal.value = true
  newPressLink.value = pressLinks.value.find((press) => pressId === press.id)
}

const editQuote = (quoteId) => {
  isEdit.value = true
  showNewQuoteModal.value = true
  newQuote.value = quotes.value.find((quote) => quoteId === quote.id)
}

const resetPressLink = () => {
  newPressLink.value = {
    date: new Date(),
    visible: true,
  }
}

const resetQuote = () => {
  newQuote.value = {
    visible: true,
  }
}

const savePressLink = async () => {
  try {
    const data = { ...newPressLink.value }
    if (data.id) {
      const docRef = doc(pressCollection, data.id)
      await updateDoc(docRef, data)
    } else {
      await addDoc(pressCollection, data)
    }
    showToast('Successfully Saved Press Link', 'success')
    showNewPressLinkModal.value = false
    resetPressLink()
  } catch (err) {
    console.error('Failed to Save Press Link:', err)
    showToast('Failed to Save Press Link', 'error')
    showNewPressLinkModal.value = false
  }
}

const saveQuote = async () => {
  try {
    const data = { ...newQuote.value }
    if (data.id) {
      const docRef = doc(quotesCollection, data.id)
      await updateDoc(docRef, data)
    } else {
      await addDoc(quotesCollection, data)
    }
    showToast('Successfully Saved Quote', 'success')
    showNewQuoteModal.value = false
    resetQuote()
  } catch (err) {
    console.error('Failed to Save Quote:', err)
    showToast('Failed to Save Quote', 'error')
    showNewQuoteModal.value = false
  }
}
</script>

<style scoped>
.press-links .secondary-text-color {
  color: #68b8c3;
}
@media screen and (max-width: 600px) {
  .press-links .quotes {
    flex-direction: column;
  }
}
</style>
